import { useState, useEffect, useRef } from 'react'

import { signIn, signOut, useSession } from 'next-auth/react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useRouter } from 'next/router'

import Seed from '@/components/atoms/Seed'
import Footer from '@/components/organisms/Footer'
import Header from '@/components/organisms/Header'
import SocialForm from '@/components/organisms/SocialForm'
import SelectSeed from '@/components/organisms/SelectSeed'
import WishPreview from '@/components/organisms/WishPreview'
import FinishWish from '@/components/organisms/FinishWish'
import CreateWish from '@/components/organisms/CreateWish'
import PlantsAnimation from '@/components/organisms/PlantsAnimation'
import Share from '@/components/organisms/Share'
import SocialLogged from '@/components/organisms/SocialLogged'
import { usePostWish } from '@/services/wish/hooks'
import routes from '@/config/routes'
import PlantTypeInterface from '@/model/interfaces/plant-type'
import { removeAxiosToken } from '@/services/index'
import { getSeedStorage, getWishStorage, removeWishStorage, setSeedStorage } from '@/utils/index'
import { getReverseGeocode } from '@/services/geocode/api'
import { DEFAULT_LOCALE } from '@/config/constants'
import { WishPlanted } from '@/model/interfaces/wish'

import ComponentStyled from './styled'

const STEPS = {
  social: 1,
  socialLogged: 2,
  selectSeed: 3,
  createWish: 4,
  wishPreview: 5,
  thanks: 6,
  share: 7,
}

const Wish = () => {
  const { t } = useTranslation()
  const location = t('Citizen of the world')

  const router = useRouter()
  const stepsRef = useRef<HTMLDivElement>(null)
  const bottomImagesRef = useRef<HTMLDivElement>(null)
  const [step, setStep] = useState(STEPS.social)
  const [visibleSeed, setVisibleSeed] = useState(true)

  const [wish, setWish] = useState('')
  const [userLocation, setUserLocation] = useState(location)

  const [seed, setSeed] = useState<PlantTypeInterface>()

  const [wishPlanted, setWishPlanted] = useState<WishPlanted | undefined>()
  const { data: session } = useSession()

  const userName = session?.user?.name || ''
  const userImage = session?.user?.image || ''

  const { mutate: mutatePostWish } = usePostWish({
    onSuccess: data => {
      removeWishStorage()
      setWishPlanted(data)
      setStep(STEPS.thanks)
    },
    onError: err => {
      if (err.message.includes('403')) {
        toast.error(t('You reached the limit of 2 wishes per person'))
      } else {
        toast.error(t('An error has occurred'))
      }

      router.push(routes.home)
    },
  })

  useEffect(() => {
    if ('geolocation' in navigator) {
      const successCallback: PositionCallback = async position => {
        if (!position || !position.coords || !position.coords.latitude) {
          return
        }

        try {
          const { city, countryName } = await getReverseGeocode(
            position.coords.latitude,
            position.coords.longitude,
            DEFAULT_LOCALE
          )
          let userLocation = ''
          if (city && countryName) {
            userLocation = `${city}, ${countryName}`
          } else if (city) {
            userLocation = city
          } else if (countryName) {
            userLocation = countryName
          } else {
            userLocation = location
          }
          setUserLocation(userLocation)
        } catch {}
      }

      navigator.geolocation.getCurrentPosition(successCallback)
    }
  }, [location])

  useEffect(() => {
    if (session) {
      setStep(STEPS.socialLogged)
    }
  }, [session])

  useEffect(() => {
    if (router.query.error === 'OAuthAccountNotLinked') {
      toast.error(t('This email address is already in use. Please, use another email address to continue'))
    }
  }, [router, t])

  const handleConfirmSocial = (socialId: string) => {
    signIn(socialId)
  }

  const handleConfirmSocialLogged = () => {
    const wishStorage = getWishStorage()
    const selectedSeedStorage = getSeedStorage()

    if (selectedSeedStorage && wishStorage) {
      setSeed(selectedSeedStorage)
      setStep(STEPS.createWish)
    } else {
      setStep(STEPS.selectSeed)
    }
  }

  const handleConfirmSeed = (plant: PlantTypeInterface) => {
    setSeedStorage(plant)
    setSeed(plant)
    setStep(STEPS.createWish)
  }

  const handleConfirmCreateWish = (text: string) => {
    setWish(text)
    setStep(STEPS.wishPreview)
  }

  const handleConfirmWishPreview = () => {
    if (seed) {
      const userInfo = {
        name: session?.user?.name || '',
        location: userLocation,
        image_url: session?.user?.image || '',
      }

      mutatePostWish({ text: wish, plant_type: seed.id, user_info: userInfo })
    }
  }

  const handleConfirmFinishWish = () => {
    setStep(STEPS.share)
  }

  const handleCancelSocialLogged = () => {
    removeAxiosToken()
    removeWishStorage()
    signOut()
  }

  const renderSteps = () => {
    switch (step) {
      case STEPS.social:
        return <SocialForm onConfirm={handleConfirmSocial} />
      case STEPS.socialLogged:
        return <SocialLogged onConfirm={handleConfirmSocialLogged} onCancel={handleCancelSocialLogged} />
      case STEPS.selectSeed:
        return <SelectSeed onConfirm={handleConfirmSeed} />
      case STEPS.createWish:
        return <CreateWish onConfirm={handleConfirmCreateWish} />
      case STEPS.wishPreview:
        return (
          <WishPreview
            onConfirm={handleConfirmWishPreview}
            user={userName}
            avatar={userImage}
            wish={wish}
            location={userLocation}
          />
        )
      case STEPS.thanks:
        return (
          <FinishWish
            onConfirm={handleConfirmFinishWish}
            user={userName}
            avatar={userImage}
            wish={wish}
            location={userLocation}
          />
        )
      case STEPS.share:
        return <Share wishPlanted={wishPlanted!} />
    }
  }

  const startAnimation = step === STEPS.thanks || step === STEPS.share
  const seedImage = seed?.attributes?.icon?.data?.attributes?.url
  const stepsOffsetTop = stepsRef?.current?.offsetTop || 0
  const stepsClientHeight = stepsRef?.current?.clientHeight || 0
  const bottomImagesPosition = bottomImagesRef?.current?.offsetTop || 0
  const seedPosition = bottomImagesPosition - (stepsOffsetTop + stepsClientHeight)

  const animationUrl = seed?.attributes?.animation?.data?.attributes?.url

  return (
    <ComponentStyled
      startAnimation={startAnimation}
      seedPosition={seedPosition}
      bottomImagesPosition={bottomImagesPosition}
    >
      <Header showBack />
      <div className='global-page-wrapper'>
        <div className='steps' ref={stepsRef}>
          {renderSteps()}
          {visibleSeed && (
            <div className='seed-prev-img'>{seedImage && <Seed url={seedImage} title={seed.attributes.title} />}</div>
          )}
        </div>
        <div className='bottom-images' ref={bottomImagesRef}>
          <PlantsAnimation
            onPlayAnimation={() => setVisibleSeed(false)}
            showSemiCircle={seed ? true : false}
            startAnimation={startAnimation}
            animationUrl={animationUrl}
          />
        </div>
      </div>
      <Footer />
    </ComponentStyled>
  )
}

export default Wish
